import { useCallback, useState } from 'react';

import { BookIcon, ExternalLinkIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { McpIcon } from '@/components/atoms/Icons/McpIcon';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { Button } from '@/components/ui/button';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import useBrains from '@/hooks/useBrains';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import { useSidebar } from '@/hooks/useSidebar';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { Tool } from '@/shared/types/brain';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { MCP_DOCS_URL, TABLE_LAYOUT_FILL_CLASSES } from '@/util/constants';
import { removeAllReferencesOfToolFromGuidelines } from '@/util/guidelineToolReferences';

import { DeleteMcpServerDialog } from './DeleteMcpServerDialog';
import useMcpServers, { type McpServer, type McpTool } from './hooks/useMcpServers';
import LocalToolsTab from './LocalToolsTab';
import { McpServersTable } from './McpServersTable';
import { getAuthHeaders, ResyncDialog } from './ResyncDialog';

const TAB_MCP = 'mcp';
const TAB_LOCAL = 'local';

export default function MCPs() {
  useSegments(PageName.MCPS);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const brainId = useSelector(selectBrainId);
  const { isOpen, meta: sidebarMeta, open: openPanel } = useSidebar();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const { mcpServers, listStatus, updateMcpServer, deleteMcpServer, testMutation } = useMcpServers(
    brainId ?? undefined
  );
  const { brain, updateBrain, patchBrainGuidelinesAsync } = useBrains(brainId ?? undefined);

  const [resyncServer, setResyncServer] = useState<McpServer | null>(null);
  const [deleteServer, setDeleteServer] = useState<McpServer | null>(null);

  const activeTab = searchParams.get('tab') === TAB_LOCAL ? TAB_LOCAL : TAB_MCP;
  const activeMcpId = isOpen('edit_mcp') ? sidebarMeta?.mcpId : undefined;

  const handleAddMcp = () => {
    openPanel('create_mcp');
  };

  const handleToggleActive = useCallback(
    (server: McpServer, isActive: boolean) => {
      updateMcpServer({ brain_id: server.brain_id, mcp_id: server.mcp_id, is_active: isActive });
    },
    [updateMcpServer]
  );

  const handleDelete = useCallback((server: McpServer) => {
    setDeleteServer(server);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteServer) {
      deleteMcpServer(deleteServer.mcp_id);
      setDeleteServer(null);
    }
  }, [deleteServer, deleteMcpServer]);

  const handleResync = useCallback(
    (server: McpServer) => {
      testMutation.reset();
      setResyncServer(server);
      const headers = getAuthHeaders(server);
      testMutation.mutate({ url: server.url, headers });
    },
    [testMutation]
  );

  const handleResyncUpdate = useCallback(
    (tools: McpTool[]) => {
      if (!resyncServer) {
        return;
      }
      updateMcpServer({
        brain_id: resyncServer.brain_id,
        mcp_id: resyncServer.mcp_id,
        tools,
        tools_status: 'ok',
      });
      setResyncServer(null);
    },
    [resyncServer, updateMcpServer]
  );

  const handleResyncDialogChange = useCallback((open: boolean) => {
    if (!open) {
      setResyncServer(null);
    }
  }, []);

  const handleEdit = useCallback(
    (server: McpServer) => {
      openPanel('edit_mcp', { mcpId: server.mcp_id });
    },
    [openPanel]
  );

  const handleSelect = useCallback(
    (server: McpServer) => {
      navigate(`${pathname}/${server.mcp_id}`);
    },
    [navigate, pathname]
  );

  const handleToggleTool = useCallback(
    (tool: Tool, enabled: boolean) => {
      if (!brain) {
        return;
      }
      const currentTools = brain.tools ?? [];
      const nextTools = enabled ? [...currentTools, tool] : currentTools.filter((t) => t !== tool);
      updateBrain({ brain_id: brain.brain_id, tools: nextTools, preventToast: true });
    },
    [brain, updateBrain]
  );

  const handleCleanupToolReferences = useCallback(
    (tool: Tool) => {
      if (!brain?.brain_id) {
        return;
      }
      const patch = removeAllReferencesOfToolFromGuidelines(brain.guidelines, tool);
      if (Object.keys(patch).length > 0) {
        patchBrainGuidelinesAsync({ brain_id: brain.brain_id, preventToast: true, ...patch });
      }
    },
    [brain?.brain_id, brain?.guidelines, patchBrainGuidelinesAsync]
  );

  const isLoading = listStatus === 'pending';
  const { showEmpty, showEmptySkeleton, showList } = useEmptyStateView({
    scope: `mcp:${brainId}`,
    isLoading,
    isEmpty: mcpServers.length === 0,
    isError: listStatus === 'error',
  });

  const mcpContent = (
    <div className={cn(TABLE_LAYOUT_FILL_CLASSES, 'gap-4')}>
      {showEmpty && (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <McpIcon aria-hidden />
            </EmptyMedia>
            <EmptyTitle>{t('mcp.title')}</EmptyTitle>
            <EmptyDescription>{t('mcp.empty_description')}</EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button size="sm" onClick={() => handleAddMcp()} disabled={!canWrite}>
              <PlusIcon size={16} />
              {t('common.add')}
            </Button>

            <a
              href={MCP_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:underline"
            >
              <span className="text-sm leading-6 font-medium">{t('common.learn_more')}</span>
              <ExternalLinkIcon className="h-4 w-4 text-foreground-muted" />
            </a>
          </EmptyContent>
        </Empty>
      )}

      {showEmptySkeleton && <EmptySkeleton media="icon" descriptionLines={4} link />}

      {showList && (
        <>
          <ContextualHelp
            title={t('mcp.contextual_help_title')}
            name="about_mcp_servers"
            links={[{ icon: <BookIcon size={16} />, label: t('docs.docs'), url: MCP_DOCS_URL }]}
          >
            {t('mcp.contextual_help_message')}
          </ContextualHelp>

          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="pl-1 text-xl font-semibold tracking-tight text-foreground">
              {t('mcp.title')}
            </h2>
            <Button size="sm" onClick={() => handleAddMcp()} disabled={!canWrite}>
              <PlusIcon className="size-4" />
              {t('common.add')}
            </Button>
          </div>

          {/* Table */}
          <McpServersTable
            className="min-h-0 flex-1"
            mcpServers={mcpServers}
            isLoading={isLoading}
            canWrite={canWrite}
            activeRowId={activeMcpId}
            onToggleActive={handleToggleActive}
            onDelete={handleDelete}
            onResync={handleResync}
            onEdit={handleEdit}
            onSelect={handleSelect}
          />
        </>
      )}
    </div>
  );

  return (
    <PageContentWrapper noGutters>
      {activeTab === TAB_LOCAL && brain ? (
        <LocalToolsTab
          guidelines={brain.guidelines}
          tools={brain.tools}
          canWrite={canWrite}
          onToggleTool={handleToggleTool}
          onCleanupToolReferences={handleCleanupToolReferences}
        />
      ) : (
        mcpContent
      )}

      <ResyncDialog
        open={!!resyncServer}
        onOpenChange={handleResyncDialogChange}
        server={resyncServer}
        testResult={testMutation.data}
        isLoading={testMutation.isPending}
        error={testMutation.error}
        onUpdate={handleResyncUpdate}
      />

      <DeleteMcpServerDialog
        server={deleteServer}
        onOpenChange={(open) => !open && setDeleteServer(null)}
        onConfirm={handleConfirmDelete}
      />
    </PageContentWrapper>
  );
}
